import { Shapes } from "../util/Shapes";
import Vector2 from "./Vector2";

export namespace Physics {

    export function PointInCircle(point: Vector2, circle: Shapes.Circle) {

        return Vector2.distance(point, circle.position) <= circle.radius;

    }

    export function CircleCircleCollision(a: Shapes.Circle, b: Shapes.Circle) {
        const maxDist: number = a.radius + b.radius;
        const dist: number = Vector2.distance(a.position, b.position);

        if (dist > maxDist) return false;

        return maxDist - dist;
    }

    export function PointInRect(point: Vector2, rect: Shapes.Rect) {
        const topleft = rect.center.sub(rect.size.scale(0.5));

        return point.x >= topleft.x && point.x <= topleft.x + rect.size.x && point.y >= topleft.y && point.y <= topleft.y + rect.size.y;
    }

    export function CircleRectCollision(circle: Shapes.Circle, rect: Shapes.Rect) {
        for (let theta = 0; theta < 2 * Math.PI; theta += (2 * Math.PI)/128) {
            const vec: Vector2 = new Vector2(
                Math.cos(theta),
                Math.sin(theta)
            );
            const testPos: Vector2 = circle.position.add(vec.scale(circle.radius));
            const collision = PointInRect(testPos, rect);

            if (collision) return true;
        }

        return false;
    }

    export function LineLineCollision(a: Shapes.Line, b: Shapes.Line) {
        const x1 = a.start.x; const y1 = a.start.y;
        const x2 = a.end.x; const y2 = a.end.y;
        const x3 = b.start.x; const y3 = b.start.y;
        const x4 = b.end.x; const y4 = b.end.y;

        const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);

        if (denom === 0) return false;

        const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4))/denom;
        const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3))/denom;

        if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
            return new Vector2((x2 - x1) * t + x1, (y2 - y1) * t + y1);
        }

        return false;
    }

    export function LineRectCollision(line: Shapes.Line, rect: Shapes.Rect) {
        const topleft = rect.center.sub(rect.size.scale(0.5));
        const topright = topleft.add(Vector2.right.scale(rect.size.x));
        const bottomright = topright.add(Vector2.down.scale(rect.size.y));
        const bottomleft = topleft.add(Vector2.down.scale(rect.size.y));
        const rline1: Shapes.Line = {start: topleft, end: topright};
        const rline2: Shapes.Line = {start: topright, end: bottomright};
        const rline3: Shapes.Line = {start: topleft, end: bottomleft};
        const rline4: Shapes.Line = {start: bottomleft, end: bottomright};

        for (const rline of [rline1, rline2, rline3, rline4]) {
            const collision = LineLineCollision(line, rline);
            if (collision) return collision;
        }

        return false;
    }

    export function RectRectCollision(a: Shapes.Rect, b: Shapes.Rect) {
        const atl = a.center.sub(a.size.scale(0.5));
        const btl = b.center.sub(b.size.scale(0.5));
        const x1 = atl.x;
        const x2 = atl.x + a.size.x;
        const y1 = atl.y;
        const y2 = atl.y + a.size.y;
        const x3 = btl.x;
        const x4 = btl.x + b.size.x;
        const y3 = btl.y;
        const y4 = btl.y + b.size.y;

        return x1 <= x4 && x3 <= x2 && y1 <= y4 && y3 <= y2;
    }

    export function LineShapeCollision(line: Shapes.Line, shape: Vector2[]): Vector2|undefined {
        const shapeLines: Shapes.Line[] = [];

        for (let i = 0; i < shape.length-1; i++) {
            shapeLines.push({
                start: shape[i].clone(),
                end: shape[i+1].clone()
            });
        }

        shapeLines.push({
            start: shape[shape.length-1].clone(),
            end: shape[0].clone()
        });

        const hits: Vector2[] = [];

        for (const rline of shapeLines) {
            const result = LineLineCollision(line, rline);

            if (result) hits.push(result);
        }

        hits.sort((a,b) => {
            return Vector2.distance(a, line.start) - Vector2.distance(b, line.start);
        });

        return hits[0];
    }

    export function PointOnLine(point: Vector2, line: Shapes.Line) {
        const lineLen = line.end.sub(line.start).magnitude;
        const d1 = Vector2.distance(point, line.start);
        const d2 = Vector2.distance(point, line.end);

        return Math.abs((d1+d2) - lineLen) <= 0.1;
    }

    export function CircleLineCollision(circle: Shapes.Circle, line: Shapes.Line) {
        if (PointInCircle(line.start, circle) || PointInCircle(line.end, circle)) return true;
        const cx = circle.position.x;
        const cy = circle.position.y;
        const x1 = line.start.x;
        const y1 = line.start.y;
        const x2 = line.end.x;
        const y2 = line.end.y;
        const lineLen = line.end.sub(line.start).magnitude;
        const dot = ( (cx-x1)*(x2-x1) + (cy-y1)*(y2-y1) ) / Math.pow(lineLen,2);
        const closestX = x1 + (dot * (x2-x1));
        const closestY = y1 + (dot * (y2-y1));
        const closestPointOnLine = new Vector2(closestX, closestY)

        if (!PointOnLine(closestPointOnLine, line)) return false;

        if (PointInCircle(closestPointOnLine, circle)) return true;
    }

    export function CircleShapeCollision(circle: Shapes.Circle, shape: Vector2[]) {
        const shapeLines: Shapes.Line[] = [];

        for (let i = 0; i < shape.length-1; i++) {
            shapeLines.push({
                start: shape[i].clone(),
                end: shape[i+1].clone()
            });
        }

        shapeLines.push({
            start: shape[shape.length-1].clone(),
            end: shape[0].clone()
        });

        for (const line of shapeLines) {
            if (CircleLineCollision(circle, line)) return true;
        }

        return false;
    }

}